import {
    CAPTURE_CONFIG_SCHEMA_VERSION,
    type ContributionCaptureConfigV1,
    type ContributionCaptureScopesV1,
    type NormalizedContributionCaptureConfigV1,
} from "./types";

const DEFAULT_FLUSH_INTERVAL_MS = 30_000;
const DEFAULT_MAX_BATCH_SIZE = 100;
const DEFAULT_MAX_QUEUE_ITEMS = 10_000;
export const CONTRIBUTION_CAPTURE_STORAGE_KEY = "libretto:instrumentation:capture-config:v1";

const disabledScopes = (): ContributionCaptureScopesV1 => ({
    semanticEvents: false,
    projectCheckpoints: false,
    sourceMediaReferences: false,
    renderReferences: false,
    contentFields: false,
});

export const DISABLED_CONTRIBUTION_CAPTURE_CONFIG: NormalizedContributionCaptureConfigV1 = {
    schemaVersion: CAPTURE_CONFIG_SCHEMA_VERSION,
    enabled: false,
    captureConfigId: "",
    consentScopeId: "",
    profile: "organic_semantic_v1",
    scopes: disabledScopes(),
    flushIntervalMs: DEFAULT_FLUSH_INTERVAL_MS,
    maxBatchSize: DEFAULT_MAX_BATCH_SIZE,
    maxQueueItems: DEFAULT_MAX_QUEUE_ITEMS,
};

export function contributionCaptureAuthorityKey(
    config: NormalizedContributionCaptureConfigV1,
): string {
    return JSON.stringify({
        schemaVersion: config.schemaVersion,
        enabled: config.enabled,
        profile: config.profile,
        captureConfigId: config.captureConfigId,
        consentScopeId: config.consentScopeId,
        scopes: {
            semanticEvents: config.scopes.semanticEvents,
            projectCheckpoints: config.scopes.projectCheckpoints,
            sourceMediaReferences: config.scopes.sourceMediaReferences,
            renderReferences: config.scopes.renderReferences,
            contentFields: config.scopes.contentFields,
        },
        allowedProjectIds: [...(config.allowedProjectIds ?? [])].sort(),
        effectiveAt: config.effectiveAt ?? null,
        expiresAt: config.expiresAt ?? null,
    });
}

const boundedInteger = (value: unknown, fallback: number, min: number, max: number): number => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.max(min, Math.min(max, Math.floor(parsed)));
};

const optionalIsoDate = (value: unknown): string | undefined => {
    if (typeof value !== "string" || !value.trim()) return undefined;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? new Date(parsed).toISOString() : undefined;
};

const normalizeScopes = (raw: unknown): ContributionCaptureScopesV1 => {
    const value = raw && typeof raw === "object" ? raw as Record<string, unknown> : {};
    return {
        semanticEvents: value.semanticEvents === true,
        projectCheckpoints: value.projectCheckpoints === true,
        sourceMediaReferences: value.sourceMediaReferences === true,
        renderReferences: value.renderReferences === true,
        contentFields: value.contentFields === true,
    };
};

export function normalizeContributionCaptureConfig(
    raw: unknown,
): NormalizedContributionCaptureConfigV1 {
    if (!raw || typeof raw !== "object") {
        return { ...DISABLED_CONTRIBUTION_CAPTURE_CONFIG, scopes: disabledScopes() };
    }

    const value = raw as Record<string, unknown>;
    const schemaVersion = Number(value.schemaVersion ?? value.schema_version);
    const captureConfigId = String(value.captureConfigId ?? value.capture_config_id ?? "").trim();
    const consentScopeId = String(value.consentScopeId ?? value.consent_scope_id ?? "").trim();
    const profile = String(value.profile ?? "").trim();
    const scopes = normalizeScopes(value.scopes);

    const structurallyValid =
        schemaVersion === CAPTURE_CONFIG_SCHEMA_VERSION &&
        value.enabled === true &&
        captureConfigId.length > 0 &&
        consentScopeId.length > 0 &&
        profile === "organic_semantic_v1" &&
        scopes.semanticEvents;

    if (!structurallyValid) {
        return { ...DISABLED_CONTRIBUTION_CAPTURE_CONFIG, scopes: disabledScopes() };
    }

    const rawAllowedProjectIds = value.allowedProjectIds ?? value.allowed_project_ids;
    const allowedProjectIds = Array.isArray(rawAllowedProjectIds)
        ? Array.from(new Set(rawAllowedProjectIds.map((id) => String(id ?? "").trim()).filter(Boolean)))
        : undefined;

    return {
        schemaVersion: CAPTURE_CONFIG_SCHEMA_VERSION,
        enabled: true,
        captureConfigId,
        consentScopeId,
        profile: "organic_semantic_v1",
        scopes,
        ...(allowedProjectIds ? { allowedProjectIds } : {}),
        ...(optionalIsoDate(value.effectiveAt ?? value.effective_at)
            ? { effectiveAt: optionalIsoDate(value.effectiveAt ?? value.effective_at) }
            : {}),
        ...(optionalIsoDate(value.expiresAt ?? value.expires_at)
            ? { expiresAt: optionalIsoDate(value.expiresAt ?? value.expires_at) }
            : {}),
        flushIntervalMs: boundedInteger(
            value.flushIntervalMs ?? value.flush_interval_ms,
            DEFAULT_FLUSH_INTERVAL_MS,
            5_000,
            5 * 60_000,
        ),
        maxBatchSize: boundedInteger(
            value.maxBatchSize ?? value.max_batch_size,
            DEFAULT_MAX_BATCH_SIZE,
            1,
            100,
        ),
        maxQueueItems: boundedInteger(
            value.maxQueueItems ?? value.max_queue_items,
            DEFAULT_MAX_QUEUE_ITEMS,
            100,
            100_000,
        ),
    } satisfies ContributionCaptureConfigV1;
}

export function isContributionCaptureAllowedForProject(
    config: NormalizedContributionCaptureConfigV1,
    projectId: string,
    nowMs: number = Date.now(),
): boolean {
    if (!config.enabled || !config.scopes.semanticEvents || !projectId) return false;
    if (config.effectiveAt && nowMs < Date.parse(config.effectiveAt)) return false;
    if (config.expiresAt && nowMs >= Date.parse(config.expiresAt)) return false;
    if (config.allowedProjectIds && !config.allowedProjectIds.includes(projectId)) return false;
    return true;
}

interface StorageReader {
    getItem(key: string): string | null;
}

export function resolveContributionCaptureConfig(args?: {
    remoteConfig?: unknown;
    hasRemoteConfig?: boolean;
    locationSearch?: string;
    storage?: StorageReader | null;
}): NormalizedContributionCaptureConfigV1 {
    // An explicitly supplied remote value is authoritative, including a disabled value.
    // This lets revocation/kill-switch state override local pilot settings.
    if (args?.hasRemoteConfig) {
        return normalizeContributionCaptureConfig(args.remoteConfig);
    }

    try {
        const params = new URLSearchParams(args?.locationSearch ?? "");
        const profile = String(params.get("librettoCaptureProfile") ?? "").trim();
        const captureConfigId = String(params.get("librettoCaptureConfigId") ?? "").trim();
        const consentScopeId = String(params.get("librettoConsentScopeId") ?? "").trim();
        if (profile || captureConfigId || consentScopeId) {
            const projectId = String(params.get("librettoCaptureProjectId") ?? "").trim();
            return normalizeContributionCaptureConfig({
                schemaVersion: 1,
                enabled: true,
                profile,
                captureConfigId,
                consentScopeId,
                scopes: {
                    semanticEvents: true,
                    projectCheckpoints: params.get("librettoCaptureCheckpoints") !== "0",
                    sourceMediaReferences: false,
                    renderReferences: false,
                    contentFields: false,
                },
                ...(projectId ? { allowedProjectIds: [projectId] } : {}),
            });
        }
    } catch {}

    try {
        const raw = args?.storage?.getItem(CONTRIBUTION_CAPTURE_STORAGE_KEY);
        if (raw) return normalizeContributionCaptureConfig(JSON.parse(raw));
    } catch {}

    return { ...DISABLED_CONTRIBUTION_CAPTURE_CONFIG, scopes: disabledScopes() };
}
